(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Lightbox = global.NewHomeSource.Lightbox || {};

  class ExternalMediaHandler {
    /**
     * Initializes a new instance of the class.
     * External media handler manages the creation and removal of iframes
     * for media items that are not videos
     * (e.g., 3d Tours and other embedded content from other platforms).
     * Sets up initial state, media iframes, index, and event observers.
     */
    constructor() {
      this.state = null;
      this.iframes = {};
      this.currentIndex = null;
      this.eventsToObserve = {
        itemsChange: this.itemsChange.bind(this),
      };
    }

    /**
     * Initializes the media handler with the provided state and sets up observers.
     * @param {Object} state - The initial state to be used by the handler.
     * @returns {this} The instance of the media handler for chaining.
     */
    initialize(state) {
      this.state = state;
      this.addObservers();
      return this;
    }

    /**
     * Retrieves media slice elements from the DOM based on the provided column indexes.
     *
     * @param {number[]} indexes - An array of column indexes to select media slices for.
     * @returns {Element[]} An array of DOM elements corresponding to the specified column indexes.
     */
    getMediaSlices(indexes) {
      const rootNode = this.state.get('rootNode');
      return indexes.map((index) => rootNode.querySelector(`[data-column="${index}"][data-type="ExternalMedium"]`))
        .filter(Boolean);
    }

    /**
     * Handles changes to the media items, updating the current index, removing inactive iframes,
     * creating new iframe elements for the specified indexes.
     *
     * @async
     * @param {Object} data - The data object containing information about the current selection.
     * @param {number[]} [data.indexes] - Array of indexes representing the selected media slices.
     * @param {number} data.currentIndex - The index of the currently selected media item.
     * @returns {Promise<void>} Resolves when the iframe elements have been updated.
     */
    async itemsChange(data) {
      const indexes = data.indexes || [];
      if (this.currentIndex === data.currentIndex) {
        return;
      }
      this.currentIndex = data.currentIndex;

      // Remove iframes that are no longer visible
      Object.keys(this.iframes).forEach((index) => {
        if (!indexes.includes(parseInt(index, 10))) {
          this.removeIframe(index);
        }
      });

      const slices = this.getMediaSlices(indexes);
      const iframes = slices.map(this.createIframe.bind(this)).filter((result) => result.iframe !== null);
      this.setIframes(iframes);
    }

    /**
     * Updates the internal iframes collection by mapping each iframe's index to its element.
     * Existing iframes are preserved and new/updated iframes are merged in.
     *
     * @param {Array<{index: string|number, iframe: HTMLElement}>} iframes - Array of iframe objects, each containing an
     * 'index' and 'iframe' property.
     */
    setIframes(iframes) {
      this.iframes = iframes.reduce((iframeCollection, currentIframe) => Object.assign(
        {},
        iframeCollection,
        { [currentIframe.index]: currentIframe.iframe }
      ), this.iframes);
    }

    /**
     * Creates an iframe element for external media within a specified container.
     *
     * @param {HTMLElement} slice - The DOM element containing data attributes for media URL.
     * @returns {Object} An object containing the iframe element and its index.
     */
    createIframe(slice) {
      const index = parseInt(slice.dataset.column, 10);
      const container = slice.querySelector('[data-component="lightbox-media-container"]');

      if (!container) {
        return {
          iframe: null,
          index
        };
      }

      if (this.iframes[index]) {
        return {
          iframe: this.iframes[index],
          index
        };
      }

      const mediaUrl = slice.dataset.url;

      // Validate and normalize the media URL to avoid dangerous schemes (e.g., javascript:)
      let resolvedUrl;
      try {
        resolvedUrl = new URL(mediaUrl, window.location.origin);
      } catch (e) {
        // If the URL is invalid, do not create an iframe.
        return {
          iframe: null,
          index
        };
      }

      if (resolvedUrl.protocol !== 'http:' && resolvedUrl.protocol !== 'https:') {
        // Only allow http/https URLs for embedded media.
        return {
          iframe: null,
          index
        };
      }

      const iframe = document.createElement('iframe');

      iframe.setAttribute('src', resolvedUrl.toString());
      iframe.setAttribute('width', '100%');
      iframe.setAttribute('height', '100%');
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allowfullscreen', 'true');
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
      // Apply a restrictive sandbox to limit capabilities of cross-origin content.
      // Note: allow-same-origin is required for some embedded media providers (e.g., Matterport)
      // to function properly. This still provides security by restricting other capabilities.
      iframe.setAttribute('sandbox', 'allow-scripts allow-forms allow-same-origin');
      // Limit referrer information sent to the embedded resource.
      iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');

      container.innerHTML = '';
      container.appendChild(iframe);

      return {
        iframe,
        index
      };
    }

    /**
     * Removes an iframe from the DOM and the internal collection.
     *
     * @param {string|number} index - The index of the iframe to remove.
     */
    removeIframe(index) {
      const rootNode = this.state.get('rootNode');
      const element = rootNode.querySelector(`[data-column="${index}"]`);

      if (element) {
        const container = element.querySelector('[data-component="lightbox-media-container"]');
        if (container) {
          container.innerHTML = '';
        }
      }

      delete this.iframes[index];
    }

    /**
     * Cleans up media components by removing all iframe instances,
     * clearing their associated DOM containers, resetting the iframes object,
     * and removing any observers.
     */
    destroy() {
      Object.keys(this.iframes).forEach((index) => {
        this.removeIframe(index);
      });
      this.iframes = {};
      this.removeObservers();
    }

    /**
     * Registers this instance as an observer for each event specified in `eventsToObserve`.
     * Iterates over all event keys and adds this instance as an observer to the state for each event.
     */
    addObservers() {
      Object.keys(this.eventsToObserve).forEach((event) => this.state.addObserver(event, this));
    }

    /**
     * Removes this instance as an observer from all events listed in `eventsToObserve`.
     * Iterates over each event and calls `removeObserver` on the state object.
     */
    removeObservers() {
      Object.keys(this.eventsToObserve).forEach((event) => this.state.removeObserver(event, this));
    }

    /**
     * Updates the handler by invoking the callback associated with the given topic.
     *
     * @param {*} data - The data to pass to the callback function.
     * @param {string} topic - The topic whose callback should be invoked.
     */
    update(data, topic) {
      const callback = this.eventsToObserve[topic];
      if (callback) {
        callback(data);
      }
    }
  }

  global.NewHomeSource.Lightbox.ExternalMediaHandler = ExternalMediaHandler;
}(window));
